/**
 * Action Engine — pure, Redux-independent execution of an Action[] chain.
 *
 * Usage:
 *   const result = executeActions(actions, playState);
 *   // result: { playState: PlayState, alerts: string[] }
 *
 * Caller is responsible for committing the returned playState to whatever
 * state layer it lives in (local React state in ScenarioPlayer, or Redux).
 *
 * Algorithm (per action):
 *  1. If action.condition is set → evaluate; skip action if condition is false.
 *  2. set_variable  → mutate variables copy according to variableOp.
 *  3. navigate      → push current scene to history, set currentSceneId, STOP chain.
 *  4. end_scenario  → set status='ended', STOP chain.
 *  5. alert         → push message to alerts[], continue chain.
 */

// ---------------------------------------------------------------------------
// Condition evaluation
// ---------------------------------------------------------------------------

function evaluateCondition(condition, variables) {
  if (!condition || !condition.variableName) return true;

  const current = variables[condition.variableName];
  const target = condition.value;

  switch (condition.operator) {
    case "==":
      return current == target; // intentional loose eq for "0" == 0
    case "!=":
      return current != target;
    case ">":
      return Number(current) > Number(target);
    case "<":
      return Number(current) < Number(target);
    case ">=":
      return Number(current) >= Number(target);
    case "<=":
      return Number(current) <= Number(target);
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Variable mutation
// ---------------------------------------------------------------------------

function applyVariableOp(current, op, value) {
  switch (op) {
    case "add":
      return Number(current) + Number(value);
    case "subtract":
      return Number(current) - Number(value);
    case "toggle":
      return !current;
    case "set":
    default:
      return value;
  }
}

// ---------------------------------------------------------------------------
// Main executor
// ---------------------------------------------------------------------------

/**
 * @param {Action[]} actions
 * @param {PlayState} playState  — treated as immutable; a new object is returned
 * @returns {{ playState: PlayState, alerts: string[] }}
 */
export function executeActions(actions, playState) {
  if (!Array.isArray(actions) || actions.length === 0) {
    return { playState, alerts: [] };
  }

  let state = {
    ...playState,
    variables: { ...playState.variables },
    history: [...(playState.history || [])],
    elementVisibility: { ...playState.elementVisibility },
  };

  const alerts = [];

  for (const action of actions) {
    // 1. Condition gate
    if (!evaluateCondition(action.condition, state.variables)) {
      continue;
    }

    switch (action.type) {
      case "set_variable": {
        if (!action.variableName) break;
        const current = state.variables[action.variableName] ?? 0;
        state.variables = {
          ...state.variables,
          [action.variableName]: applyVariableOp(
            current,
            action.variableOp || "set",
            action.variableValue
          ),
        };
        break;
      }

      case "navigate": {
        if (!action.targetSceneId) break;
        state = {
          ...state,
          history: [...state.history, state.currentSceneId],
          currentSceneId: action.targetSceneId,
        };
        return { playState: state, alerts };
      }

      case "end_scenario": {
        state = { ...state, status: "ended" };
        return { playState: state, alerts };
      }

      case "alert": {
        if (action.alertMessage) alerts.push(action.alertMessage);
        break;
      }

      case "set_element_visibility": {
        if (!action.targetElementId) break;
        const currentlyVisible = state.elementVisibility[action.targetElementId] !== false;
        let nextVisible;
        if (action.visibility === "show") nextVisible = true;
        else if (action.visibility === "hide") nextVisible = false;
        else nextVisible = !currentlyVisible; // toggle
        state = {
          ...state,
          elementVisibility: { ...state.elementVisibility, [action.targetElementId]: nextVisible },
        };
        break;
      }

      default:
        break;
    }
  }

  return { playState: state, alerts };
}

// ---------------------------------------------------------------------------
// Initial PlayState factory
// ---------------------------------------------------------------------------

/**
 * Build the initial runtime state from a Scenario.
 * @param {Scenario} scenario
 * @returns {PlayState}
 */
export function initPlayState(scenario) {
  const variables = {};
  for (const v of scenario.variables || []) {
    variables[v.name] =
      v.defaultValue ?? (v.type === "number" ? 0 : v.type === "boolean" ? false : "");
  }
  const scenes = scenario.scenes || [];
  // Defensive: startSceneId may be missing or point to a deleted scene — fall back to first scene
  const startSceneId =
    scenario.startSceneId && scenes.some((s) => s.id === scenario.startSceneId)
      ? scenario.startSceneId
      : (scenes[0] && scenes[0].id) || null;
  // Build initial element visibility map — elements with hidden:true start invisible
  const elementVisibility = {};
  for (const scene of scenes) {
    for (const el of scene.elements || []) {
      elementVisibility[el.id] = el.hidden !== true;
    }
  }

  return {
    currentSceneId: startSceneId,
    variables,
    elementVisibility,
    history: [],
    status: "playing",
  };
}
